import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { PROPERTY_TYPE_LABELS } from "@/lib/realestateConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const Field = ({ label, children, className = "space-y-1.5" }) => (
  <div className={className}><Label className="text-xs">{label}</Label>{children}</div>
);

export const NewPropertyDialog = ({ workspaceId, open, onOpenChange }) => {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [form, setForm] = useState({ title: "", address: "", city: "", property_type: "piso", price: "", surface_m2: "", catastro_ref: "", seller_name: "", seller_phone: "" });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("El título es obligatorio");
    setSaving(true);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/properties`, {
        title: form.title, address: form.address, city: form.city, property_type: form.property_type,
        price: form.price ? Number(form.price) : null, surface_m2: form.surface_m2 ? Number(form.surface_m2) : null,
        catastro_ref: form.catastro_ref, seller: { name: form.seller_name, phone: form.seller_phone },
      });
      qc.invalidateQueries({ queryKey: ["properties", workspaceId] });
      toast.success("Inmueble creado");
      onOpenChange(false);
      navigate(`/space/${workspaceId}/realestate/${data.id}`);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="new-property-dialog">
        <DialogHeader>
          <DialogTitle>Nuevo inmueble</DialogTitle>
          <DialogDescription>Se creará en la etapa Preventa. Podrás completar el resto después.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <Field label="Título *">
            <Input data-testid="property-title-input" value={form.title} onChange={set("title")} placeholder="Ej: Piso 3 hab. en Calle Mayor 12" required maxLength={120} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Dirección"><Input data-testid="property-address-input" value={form.address} onChange={set("address")} placeholder="Calle, número, piso" /></Field>
            <Field label="Ciudad"><Input value={form.city} onChange={set("city")} placeholder="Madrid" /></Field>
            <Field label="Tipo">
              <Select value={form.property_type} onValueChange={set("property_type")}>
                <SelectTrigger data-testid="property-type-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(PROPERTY_TYPE_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Precio salida (€)"><Input data-testid="property-price-input" type="number" min="0" value={form.price} onChange={set("price")} placeholder="250000" /></Field>
            <Field label="Superficie (m²)"><Input type="number" min="0" value={form.surface_m2} onChange={set("surface_m2")} placeholder="90" /></Field>
            <Field label="Ref. catastral"><Input value={form.catastro_ref} onChange={set("catastro_ref")} placeholder="20 caracteres" maxLength={40} /></Field>
            <Field label="Vendedor"><Input data-testid="property-seller-input" value={form.seller_name} onChange={set("seller_name")} placeholder="Nombre" /></Field>
            <Field label="Teléfono vendedor"><Input value={form.seller_phone} onChange={set("seller_phone")} placeholder="+34 600 000 000" /></Field>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
            <Button type="submit" data-testid="property-create-submit" disabled={saving}>{saving ? "Creando…" : "Crear inmueble"}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
